import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiCall } from './enhancedClient'
import type { PaginatedResponse } from '../types/api'

// ============ 类型定义 ============

// 论坛/版块
export interface Forum {
  id: string
  name: string
  slug: string
  description: string
  icon: string
  postCount: number
  threadCount: number
  lastActivityAt: string
  createdAt: string
}

// 帖子
export interface Post {
  id: string
  forumId: string
  forumName: string
  title: string
  content: string
  type: 'general' | 'review' | 'question' | 'feedback' | 'announcement'
  authorId: string
  authorName: string
  authorAvatar?: string
  isPinned: boolean
  isLocked: boolean
  viewCount: number
  likeCount: number
  commentCount: number
  lastActivityAt: string
  createdAt: string
  updatedAt: string
}

// 评论
export interface Comment {
  id: string
  postId: string
  parentId: string | null
  content: string
  authorId: string
  authorName: string
  authorAvatar?: string
  likeCount: number
  replies?: Comment[]
  createdAt: string
  updatedAt: string
}

// 书评
export interface Review {
  id: string
  novelId: string
  novelTitle: string
  novelCover?: string
  title: string
  content: string
  rating: number
  authorId: string
  authorName: string
  authorAvatar?: string
  likeCount: number
  commentCount: number
  createdAt: string
  updatedAt: string
}

// 通知
export interface Notification {
  id: string
  type: 'comment' | 'reply' | 'like' | 'system' | 'review'
  title: string
  message: string
  link?: string
  isRead: boolean
  createdAt: string
}

// ============ 查询参数 ============

export interface PostListParams {
  forumId?: string
  page?: number
  pageSize?: number
  search?: string
  type?: string
  sortBy?: string
}

export interface ReviewListParams {
  novelId?: string
  page?: number
  pageSize?: number
  sortBy?: string
}

export interface CreatePostData {
  forumId: string
  title: string
  content: string
  type: 'general' | 'review' | 'question' | 'feedback'
}

export interface CreateCommentData {
  postId: string
  content: string
  parentId?: string
}

export interface CreateReviewData {
  novelId: string
  title: string
  content: string
  rating: number
}

// ============ API Key (for React Query) ============

export const communityKeys = {
  all: ['community'] as const,
  forums: () => [...communityKeys.all, 'forums'] as const,
  posts: (params?: PostListParams) => [...communityKeys.all, 'posts', params] as const,
  post: (id: string) => [...communityKeys.all, 'post', id] as const,
  postComments: (postId: string) => [...communityKeys.all, 'comments', postId] as const,
  reviews: (params?: ReviewListParams) => [...communityKeys.all, 'reviews', params] as const,
  review: (id: string) => [...communityKeys.all, 'review', id] as const,
  notifications: () => [...communityKeys.all, 'notifications'] as const,
  unreadCount: () => [...communityKeys.all, 'unreadCount'] as const,
}

// ============ 数据归一化辅助函数 ============

/** 将后端 snake_case 帖子的响应归一化为前端 camelCase Post */
const normalizePost = (p: any): Post => ({
  id: p.id,
  forumId: p.forum_id || p.forumId || '',
  forumName: p.forum_name || p.forumName || '',
  title: p.title || '',
  content: p.content || '',
  type: p.type || 'general',
  authorId: p.author_id || p.authorId || '',
  authorName: p.author_name || p.authorName || '',
  authorAvatar: p.author_avatar || p.authorAvatar || null,
  isPinned: p.is_pinned ?? p.isPinned ?? false,
  isLocked: p.is_locked ?? p.isLocked ?? false,
  viewCount: p.view_count ?? p.viewCount ?? 0,
  likeCount: p.like_count ?? p.likeCount ?? 0,
  commentCount: p.comment_count ?? p.commentCount ?? 0,
  lastActivityAt: p.last_activity_at || p.lastActivityAt || p.created_at || '',
  createdAt: p.created_at || p.createdAt || '',
  updatedAt: p.updated_at || p.updatedAt || '',
})

/** 将后端 snake_case 评论归一化 */
const normalizeComment = (c: any): Comment => ({
  id: c.id,
  postId: c.post_id || c.postId || '',
  parentId: c.parent_id || c.parentId || null,
  content: c.content || '',
  authorId: c.author_id || c.authorId || '',
  authorName: c.author_name || c.authorName || '',
  authorAvatar: c.author_avatar || c.authorAvatar || null,
  likeCount: c.like_count ?? c.likeCount ?? 0,
  replies: c.replies ? c.replies.map(normalizeComment) : undefined,
  createdAt: c.created_at || c.createdAt || '',
  updatedAt: c.updated_at || c.updatedAt || '',
})

/** 将后端 snake_case 书评归一化 */
const normalizeReview = (r: any): Review => ({
  id: r.id,
  novelId: r.novel_id || r.novelId || '',
  novelTitle: r.novel_title || r.novelTitle || '',
  novelCover: r.novel_cover || r.novelCover || undefined,
  title: r.title || '',
  content: r.content || '',
  rating: r.rating ?? 0,
  authorId: r.user_id || r.author_id || r.authorId || '',
  authorName: r.user_name || r.author_name || r.authorName || '',
  authorAvatar: r.user_avatar || r.author_avatar || r.authorAvatar || null,
  likeCount: r.like_count ?? r.likeCount ?? 0,
  commentCount: r.comment_count ?? r.commentCount ?? 0,
  createdAt: r.created_at || r.createdAt || '',
  updatedAt: r.updated_at || r.updatedAt || '',
})

// ============ React Query Hooks — 论坛 ============

export function useForums() {
  return useQuery({
    queryKey: communityKeys.forums(),
    queryFn: async () => {
      const response = await apiCall<any>('get', '/v1/forums')
      // 数据归一化：后端返回 {forums: [...]} → 前端需要数组
      const rawForums = response?.forums || response?.data || response?.items || response || []
      const forums = Array.isArray(rawForums) ? rawForums : []
      // 字段映射：snake_case → camelCase + 补全缺失字段
      return forums.map((f: any) => ({
        id: f.id,
        name: f.name,
        slug: f.name?.toLowerCase().replace(/\s+/g, '-') || '',
        description: f.description || '',
        icon: f.icon || '📚',
        postCount: f.post_count || f.postCount || 0,
        threadCount: f.thread_count || 0,
        lastActivityAt: f.last_activity_at || f.updated_at || f.created_at || '',
        createdAt: f.created_at || f.createdAt || '',
      })) as Forum[]
    },
  })
}

export function useCreateForum() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { name: string; description: string; icon?: string }) => {
      return apiCall('post', '/v1/forums', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.forums() })
    },
  })
}

// ============ React Query Hooks — 帖子 ============

export function usePosts(params?: PostListParams) {
  return useQuery({
    queryKey: communityKeys.posts(params),
    queryFn: async () => {
      const response = await apiCall<any>('get', '/v1/posts', undefined, { params })
      // 数据归一化：{posts: [...]} → {items, total, page, pageSize, totalPages}
      const rawPosts = response?.posts || response?.data || response?.items || []
      const items = Array.isArray(rawPosts) ? rawPosts.map(normalizePost) : []
      return {
        items,
        total: response?.total || items.length,
        page: response?.page || 1,
        pageSize: response?.page_size || response?.pageSize || 20,
        totalPages: response?.total_pages || response?.totalPages || Math.ceil((response?.total || items.length) / 20),
      }
    },
  })
}

export function usePost(id: string) {
  return useQuery({
    queryKey: communityKeys.post(id),
    queryFn: async () => {
      const response = await apiCall<any>('get', `/v1/posts/${id}`)
      return normalizePost(response)
    },
    enabled: !!id,
  })
}

export function useCreatePost() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreatePostData) => {
      return apiCall('post', '/v1/posts', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.posts() })
    },
  })
}

// ============ React Query Hooks — 评论 ============

export function usePostComments(postId: string) {
  return useQuery({
    queryKey: communityKeys.postComments(postId),
    queryFn: async () => {
      const response = await apiCall<any>('get', `/v1/posts/${postId}/comments`)
      const raw = response?.comments || response?.data || response?.items || response || []
      return Array.isArray(raw) ? raw.map(normalizeComment) : []
    },
    enabled: !!postId,
  })
}

export function useCreateComment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateCommentData) => {
      return apiCall('post', `/v1/posts/${data.postId}/comments`, data)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: communityKeys.postComments(variables.postId) })
      queryClient.invalidateQueries({ queryKey: communityKeys.post(variables.postId) })
    },
  })
}

// ============ React Query Hooks — 书评 ============

export function useReviews(params?: ReviewListParams) {
  return useQuery({
    queryKey: communityKeys.reviews(params),
    queryFn: async () => {
      const response = await apiCall<any>('get', '/v1/reviews', undefined, { params })
      const rawReviews = response?.reviews || response?.data || response?.items || []
      const items = Array.isArray(rawReviews) ? rawReviews.map(normalizeReview) : []
      return {
        items,
        total: response?.total || items.length,
        page: response?.page || 1,
        pageSize: response?.page_size || response?.pageSize || 20,
        totalPages: response?.total_pages || response?.totalPages || Math.ceil((response?.total || items.length) / 20),
      }
    },
  })
}

export function useReview(id: string) {
  return useQuery({
    queryKey: communityKeys.review(id),
    queryFn: async () => {
      const response = await apiCall<Review>('get', `/v1/reviews/${id}`)
      return response as any
    },
    enabled: !!id,
  })
}

export function useCreateReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreateReviewData) => {
      return apiCall('post', '/v1/reviews', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.reviews() })
    },
  })
}

export function useLikeReview() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (reviewId: string) => {
      return apiCall<any>('post', `/v1/reviews/${reviewId}/like`)
    },
    onSuccess: (_data, reviewId) => {
      queryClient.invalidateQueries({ queryKey: communityKeys.review(reviewId) })
      queryClient.invalidateQueries({ queryKey: communityKeys.reviews() })
    },
  })
}

// ============ React Query Hooks — 通知 ============

export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: communityKeys.notifications(),
    queryFn: async () => {
      const response = await apiCall<any>('get', '/v1/notifications')
      const rawNotifications = response?.notifications || response?.data || response?.items || []
      const items = Array.isArray(rawNotifications) ? rawNotifications.map(normalizeNotification) : []
      return {
        items,
        total: response?.total || items.length,
        unreadCount: response?.unread_count ?? items.filter((n: any) => !n.isRead).length,
        page: response?.page || 1,
        pageSize: response?.page_size || response?.pageSize || 20,
      }
    },
    enabled,
  })
}

const normalizeNotification = (n: any): Notification => ({
  id: n.id,
  type: n.type || 'system',
  title: n.title || '',
  message: n.content || n.message || '',
  link: n.link || undefined,
  isRead: n.is_read ?? n.isRead ?? false,
  createdAt: n.created_at || n.createdAt || '',
})

export function useUnreadCount(enabled = true) {
  return useQuery({
    queryKey: communityKeys.unreadCount(),
    queryFn: async () => {
      const response = await apiCall<any>('get', '/v1/notifications/unread-count')
      return response?.unread_count ?? response?.count ?? 0
    },
    refetchInterval: enabled ? 60000 : false,
    enabled,
  })
}

export function useMarkRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (notificationId?: string) => {
      if (notificationId) {
        return apiCall('patch', `/v1/notifications/${notificationId}/read`)
      }
      return apiCall('patch', '/v1/notifications/read-all')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: communityKeys.notifications() })
      queryClient.invalidateQueries({ queryKey: communityKeys.unreadCount() })
    },
  })
}

// ============ 关注/粉丝 API ============

export interface FollowStatus {
  is_following: boolean
  follower_count: number
  following_count: number
}

export function useFollowStatus(targetUserId: string | undefined) {
  return useQuery({
    queryKey: ['followStatus', targetUserId],
    queryFn: async (): Promise<FollowStatus | null> => {
      try {
        return await apiCall('get', `/v1/follows/${targetUserId}/status`)
      } catch (e: any) {
        if (e?.message === '未登录' || e?.response?.status === 401) {
          return null
        }
        throw e
      }
    },
    enabled: !!targetUserId,
  })
}

export function useFollowUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (targetUserId: string) => {
      return apiCall('post', `/v1/follows/${targetUserId}`)
    },
    onSuccess: (_data, targetUserId) => {
      queryClient.invalidateQueries({ queryKey: ['followStatus', targetUserId] })
      queryClient.invalidateQueries({ queryKey: communityKeys.notifications() })
    },
  })
}

export function useUnfollowUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (targetUserId: string) => {
      return apiCall('delete', `/v1/follows/${targetUserId}`)
    },
    onSuccess: (_data, targetUserId) => {
      queryClient.invalidateQueries({ queryKey: ['followStatus', targetUserId] })
    },
  })
}
